import React, { forwardRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";

interface Props {
  open: boolean;
  unread: number;
  raised: boolean;
  /** Height in px of the call card below; the launcher sits above it when > 0. */
  liftPx?: number;
  onClick: () => void;
}

const AssistantLauncher = forwardRef<HTMLButtonElement, Props>(({ open, unread, raised, liftPx = 0, onClick }, ref) => {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const shortcut = isMac ? "Cmd+J" : "Ctrl+J";
  return (
    <motion.button
      ref={ref}
      type="button"
      onClick={onClick}
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.6, opacity: 0 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      aria-label={open ? "Close assistant" : `Open assistant (${shortcut})${unread > 0 ? `, ${unread} unread support replies` : ""}`}
      aria-expanded={open}
      title={open ? "Close assistant" : `Ask Kavoa (${shortcut})`}
      className={`fixed right-4 z-[45] flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-[bottom] duration-200 sm:bottom-6 sm:right-6 ${
        raised ? "bottom-[5.5rem]" : "bottom-[max(1rem,env(safe-area-inset-bottom))]"
      } ${open ? "max-sm:hidden" : ""}`}
      style={{
        ...(liftPx > 0 ? { bottom: `calc(2.25rem + ${liftPx}px)` } : null),
        background: "linear-gradient(135deg, #0F5132 0%, #198754 100%)",
        boxShadow: "0 8px 32px rgba(15,81,50,0.35)",
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {open ? (
          <motion.span
            key="close"
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <X className="h-6 w-6" />
          </motion.span>
        ) : (
          <motion.span
            key="open"
            initial={{ rotate: 90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: -90, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <Sparkles className="h-6 w-6" />
          </motion.span>
        )}
      </AnimatePresence>
      {!open && unread > 0 && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white ring-2 ring-white"
        >
          {unread > 9 ? "9+" : unread}
        </motion.span>
      )}
    </motion.button>
  );
});

AssistantLauncher.displayName = "AssistantLauncher";

export default AssistantLauncher;
